import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import { useSession } from '../contexts/SessionContext';
import { useImageContext } from '../contexts/ImageContext';
import { useAudio } from '../contexts/AudioContext';
import { useDiskColor } from '../contexts/DiskColorContext';
import { useAuth } from '../contexts/AuthContext';
import { Api } from '../api';
import { submitMediaExport } from '../api/export';
import EnhancedPreview from '../components/EnhancedPreview';
import ExportModal from '../components/ExportModal';
import RouteGuard from '../components/RouteGuard';
import defaultSpinningDisk from '../images/defaultSpinningDisk.jpg';
import useScrollDirection from '../hooks/useScrollDirection';
import './css/ExportPage.css';
import '../styles/burger-menu.css';

function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const milliseconds = Math.floor((seconds % 1) * 1000);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
}

function ExportPage() {
  const navigate = useNavigate();
  const { currentSession } = useSession();
  const { currentImage, imageName } = useImageContext();
  const { audioData, updatedRegion } = useAudio();
  const { diskColor } = useDiskColor();
  const { isAuthenticated, authToken } = useAuth();
  const { scrollDirection, isAtTop } = useScrollDirection();

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportUserEmail, setExportUserEmail] = useState(null);
  const [showSpecs, setShowSpecs] = useState(false);
  const exportPromiseRef = useRef(null);

  const selectedDesign = currentSession?.design || 'Design1';
  const hasUploadedImage =
    currentImage &&
    currentImage !== defaultSpinningDisk &&
    !currentImage.includes('defaultSpinningDisk') &&
    (currentImage.startsWith('data:') || imageName !== 'example.jpg');
  const canExport = hasUploadedImage && audioData?.file && updatedRegion;

  const resolveAccountEmail = async () => {
    if (!isAuthenticated || !authToken) return null;
    try {
      const { sub: userId } = jwtDecode(authToken);
      if (!userId) return null;
      const response = await Api.get(`users/${userId}`);
      return response.data?.email || null;
    } catch {
      return null;
    }
  };

  const handleExport = async () => {
    if (!canExport) return;
    const accountEmail = await resolveAccountEmail();
    setExportUserEmail(accountEmail);
    setShowExportModal(true);
  };

  useEffect(() => {
    const handleExportEvent = () => {
      if (canExport) handleExport();
    };
    window.addEventListener('exportVideo', handleExportEvent);
    return () => window.removeEventListener('exportVideo', handleExportEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canExport, isAuthenticated, authToken]);

  const handleExportSubmit = async (email) => {
    if (exportPromiseRef.current) return exportPromiseRef.current;

    exportPromiseRef.current = (async () => {
      try {
        if (!hasUploadedImage) {
          throw new Error('Image is missing. Please upload an image first.');
        }
        if (!audioData?.file) {
          throw new Error('Audio file is missing. Please upload audio again.');
        }
        if (!updatedRegion) {
          throw new Error('Audio region is not selected. Please select an audio clip.');
        }

        const formData = new FormData();
        formData.append('backgroundColor', diskColor || '#d8116d');

        const imageResponse = await fetch(currentImage);
        if (!imageResponse.ok) {
          throw new Error('Failed to load image');
        }
        const diskImageBlob = await imageResponse.blob();
        formData.append('diskImage', diskImageBlob, 'diskImage.jpg');
        formData.append('audio', audioData.file, audioData.file.name);
        formData.append('start', formatTime(updatedRegion.start));
        formData.append('end', formatTime(updatedRegion.end));
        formData.append('duration', (updatedRegion.end - updatedRegion.start).toString());
        formData.append('email', email);

        if (currentSession?.sessionId) {
          formData.append('sessionId', currentSession.sessionId);
        }

        return await submitMediaExport(formData);
      } catch (err) {
        exportPromiseRef.current = null;
        throw err;
      }
    })();

    return exportPromiseRef.current;
  };

  const handleExportComplete = () => {
    setShowExportModal(false);
    setExportUserEmail(null);
    exportPromiseRef.current = null;
    navigate('/', { replace: true });
  };

  const handleExportClose = () => {
    setShowExportModal(false);
    setExportUserEmail(null);
    exportPromiseRef.current = null;
  };

  const handleMenuToggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    window.dispatchEvent(new CustomEvent('toggleNavigationMenu'));
  };

  return (
    <RouteGuard requiresImage={true} requiresAudio={true} redirectTo="/images">
      <div className="export-page">
        <div className="export-content">
          <div className="export-header-card">
            <div className="page-header-with-menu">
              <button
                className={`page-header-menu-button ${scrollDirection === 'down' && !isAtTop ? 'hide-on-scroll' : ''}`}
                onClick={handleMenuToggle}
                type="button"
                aria-label="Open menu"
              >
                <FontAwesomeIcon icon={faBars} />
              </button>
              <h1 className="h2 page-title-mobile">Export</h1>
              <h1 className="h2 page-title-desktop">Export</h1>
            </div>
            <p className="export-description">
              Review your design and export your video when you&apos;re ready.
            </p>
          </div>

          <div className="export-layout">
            <div className="export-preview-section">
              <EnhancedPreview design={selectedDesign} showControls={true} />
            </div>

            <div className="export-options-section">
              <div className="export-card">
                <h3 className="export-card-title">Ready to Export?</h3>
                <p className="export-card-description">
                  Your video will be rendered in high quality and sent to your email within minutes.
                </p>

                <div className="export-checklist">
                  <div className={`checklist-item ${hasUploadedImage ? 'complete' : 'incomplete'}`}>
                    <span className="checklist-icon">{hasUploadedImage ? '✓' : '○'}</span>
                    <span className="checklist-label">Image uploaded</span>
                  </div>
                  <div className={`checklist-item ${audioData?.file ? 'complete' : 'incomplete'}`}>
                    <span className="checklist-icon">{audioData?.file ? '✓' : '○'}</span>
                    <span className="checklist-label">Audio uploaded</span>
                  </div>
                  <div className={`checklist-item ${updatedRegion ? 'complete' : 'incomplete'}`}>
                    <span className="checklist-icon">{updatedRegion ? '✓' : '○'}</span>
                    <span className="checklist-label">Audio clip selected</span>
                  </div>
                </div>

                <div className="export-specs">
                  <button
                    className="specs-toggle"
                    onClick={() => setShowSpecs(!showSpecs)}
                    type="button"
                  >
                    <h4 className="specs-title">Video Specifications</h4>
                    <span className="specs-toggle-icon">{showSpecs ? '▼' : '▶'}</span>
                  </button>
                  {showSpecs && (
                    <div className="specs-list">
                      <div className="spec-item">
                        <span className="spec-label">Resolution:</span>
                        <span className="spec-value">720x720 (Square)</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Format:</span>
                        <span className="spec-value">MP4 (H.264)</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Duration:</span>
                        <span className="spec-value">
                          {updatedRegion
                            ? `${Math.round(updatedRegion.end - updatedRegion.start)}s`
                            : 'Not set'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-primary btn-lg export-button-main export-button-inline"
                  onClick={handleExport}
                  disabled={!canExport}
                  type="button"
                >
                  Export Video
                </button>

                {!canExport && (
                  <p className="export-warning">⚠️ Please complete all steps before exporting</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {showExportModal && (
          <ExportModal
            onSubmit={handleExportSubmit}
            onComplete={handleExportComplete}
            onClose={handleExportClose}
            userEmail={exportUserEmail || undefined}
          />
        )}
      </div>
    </RouteGuard>
  );
}

export default ExportPage;
